import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { Asset } from '../../../models/asset/asset';
import { AssetSearchCommand } from '../../../models/asset/assetSearchCommand';
import { AssetProxyService } from '../../../proxies/asset/asset.proxy.service';
import { WeightStationAccessUtil } from '../../../utilities/weight-station-access.util';

// AssetType id for "Hidden Weight Station" (internal adjustment stations, e.g. WST-SQR, WST-MIG)
const ASSET_TYPE_HIDDEN_WEIGHT_STATION = '6a7e22b1f373c7f984b65161';

const UNASSIGNED_AREA = 'Unassigned';

export interface StationAreaGroup {
  area: string;
  stations: Asset[];
}

interface AreaOption {
  label: string;
  value: string;
}

@Component({
  selector: 'app-stations',
  templateUrl: './stations.component.html',
  styleUrl: './stations.component.scss',
})
export class StationsComponent implements OnInit {
  private readonly assetProxyService = inject(AssetProxyService);
  private readonly router = inject(Router);
  private readonly translateService = inject(TranslateService);

  isLoading = signal(0);
  items: { label: string; route?: string }[] = [{ label: this.translateService.instant('Stations'), route: '/' }];

  // Empty selection means every area
  readonly areaFilter = new FormControl<string[]>([], { nonNullable: true });
  readonly searchFilter = new FormControl<string>('', { nonNullable: true });

  private readonly selectedAreas = toSignal(this.areaFilter.valueChanges, { initialValue: [] as string[] });
  private readonly searchTerm = toSignal(this.searchFilter.valueChanges, { initialValue: '' });

  // Only the stations this user's roles allow; the area chips and counts are built from these
  private readonly stations = signal<Asset[]>([]);

  private readonly searchedStations = computed(() => {
    const term = this.searchTerm().trim().toLowerCase();
    if (!term) {
      return this.stations();
    }
    return this.stations().filter(
      (station) => station.assetId.toLowerCase().includes(term) || (station.name ?? '').toLowerCase().includes(term),
    );
  });

  readonly areaGroups = computed<StationAreaGroup[]>(() => {
    const groups = new Map<string, Asset[]>();
    for (const station of this.searchedStations()) {
      const area = this.getArea(station);
      groups.set(area, [...(groups.get(area) ?? []), station]);
    }

    const selectedAreas = this.selectedAreas();
    return [...groups.entries()]
      .filter(([area]) => selectedAreas.length === 0 || selectedAreas.includes(area))
      .map(([area, stations]) => ({ area, stations }))
      .sort((a, b) => this.compareAreas(a.area, b.area));
  });

  readonly areaOptions = computed<AreaOption[]>(() => {
    const counts = new Map<string, number>();
    for (const station of this.stations()) {
      const area = this.getArea(station);
      counts.set(area, (counts.get(area) ?? 0) + 1);
    }

    return [...counts.keys()]
      .sort((a, b) => this.compareAreas(a, b))
      .map((area) => ({ label: `${area} · ${counts.get(area)}`, value: area }));
  });

  readonly areaCount = computed(() => this.areaOptions().length);
  readonly stationCount = computed(() => this.stations().length);

  ngOnInit() {
    const searchCommand: AssetSearchCommand = {
      assetId: 'WST-',
    };
    this.isLoading.update((count) => count + 1);
    this.assetProxyService
      .filterAssets(searchCommand)
      .pipe(finalize(() => this.isLoading.update((count) => count - 1)))
      .subscribe({
        next: (assetsResult) => {
          this.stations.set(
            (assetsResult.data ?? []).filter(
              (station) => station.assetTypeReference !== ASSET_TYPE_HIDDEN_WEIGHT_STATION && WeightStationAccessUtil.canAccess(station),
            ),
          );
        },
        error: (error) => console.error(error),
      });
  }

  redirectToWeightStation(assetId: string) {
    this.router.navigate(['weight-station-detail'], {
      queryParams: { id: assetId },
    });
  }

  private getArea(station: Asset): string {
    return station.weightStationDetails?.area?.trim() || UNASSIGNED_AREA;
  }

  // Alphabetical, with stations that have no area kept at the end
  private compareAreas(a: string, b: string): number {
    if (a === UNASSIGNED_AREA) return 1;
    if (b === UNASSIGNED_AREA) return -1;
    return a.localeCompare(b);
  }
}
